/**
 * WizardModal Component
 * Multi-step modal wizard (extracted from OnboardingWizard pattern)
 * Used for multi-step forms and setup flows
 */

import React, { useState } from 'react';
import { Modal, Steps, Button, Space } from 'antd';
import { spacing } from '../../theme/designTokens';

const { Step } = Steps;

/**
 * WizardModal Component
 *
 * @param {boolean} visible - Modal visibility
 * @param {function} onCancel - Cancel/close handler
 * @param {function} onComplete - Complete handler (called with all step data)
 * @param {string} title - Modal title
 * @param {Array} steps - Step definitions [{title, content, validate}]
 * @param {number} width - Modal width (default: 800)
 * @param {Object} initialData - Initial data for all steps
 */
function WizardModal({
  visible,
  onCancel,
  onComplete,
  title = 'Setup Wizard',
  steps = [],
  width = 800,
  initialData = {},
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepData, setStepData] = useState(initialData);

  // Get current step config
  const current = steps[currentStep] || {};

  // Navigation
  const goNext = () => {
    // Validate current step if validator provided
    if (current.validate) {
      const isValid = current.validate(stepData);
      if (!isValid) return;
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    // Validate final step
    if (current.validate) {
      const isValid = current.validate(stepData);
      if (!isValid) return;
    }

    if (onComplete) {
      onComplete(stepData);
    }

    // Reset on complete
    setCurrentStep(0);
    setStepData(initialData);
  };

  const handleCancel = () => {
    // Reset state on cancel
    setCurrentStep(0);
    setStepData(initialData);
    if (onCancel) {
      onCancel();
    }
  };

  // Update step data
  const updateStepData = (newData) => {
    setStepData((prev) => ({ ...prev, ...newData }));
  };

  // Is first/last step
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleCancel}
      width={width}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Button onClick={handleCancel}>Cancel</Button>
          </div>
          <Space>
            <Button disabled={isFirstStep} onClick={goBack}>
              Back
            </Button>
            {isLastStep ? (
              <Button type="primary" onClick={handleComplete}>
                Complete
              </Button>
            ) : (
              <Button type="primary" onClick={goNext}>
                Next
              </Button>
            )}
          </Space>
        </div>
      }
    >
      {/* Steps indicator */}
      <Steps current={currentStep} style={{ marginBottom: spacing.xl }}>
        {steps.map((step, index) => (
          <Step key={index} title={step.title} description={step.description} />
        ))}
      </Steps>

      {/* Current step content */}
      <div style={{ minHeight: 300 }}>
        {current.content && typeof current.content === 'function'
          ? current.content({ data: stepData, updateData: updateStepData })
          : current.content}
      </div>
    </Modal>
  );
}

export default WizardModal;
